
import { useNavigate } from 'react-router-dom';
import {
  Building, Check, TrendingUp, Users, ShieldCheck,
  Globe2, ArrowRight, FileText
} from 'lucide-react';
import { LandingHeader } from '../../components/layouts';
import { Footer } from '../../components/layouts';

export function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Building className="w-6 h-6 text-blue-600" />,
      title: 'For Companies',
      description: 'Streamline your halal certification journey. Submit applications, track progress, and manage compliance all in one place.'
    },
    {
      icon: <Users className="w-6 h-6 text-indigo-600" />,
      title: 'For Consultants',
      description: 'Manage multiple clients efficiently. Conduct pre-audits, generate reports, and guide your clients to success.'
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-green-600" />,
      title: 'For Auditors',
      description: 'Perform digital audits with ease. Access checklists, manage non-conformance reports, and ensure rigorous standards.'
    }
  ];

  const stats = [
    { value: '25%', label: 'Global Population' },
    { value: '$3T+', label: 'Market Value by 2028' },
    { value: '1000+', label: 'Certified Companies' },
    { value: '50+', label: 'Countries Served' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-green-50/50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Now Live: HALMAS Beta
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            Integrity in Every Step, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
              Compliance with Confidence.
            </span>
          </h1>
          
          <p className="max-w-2xl mx-auto text-xl text-gray-600 mb-10 leading-relaxed">
            The complete ecosystem for Halal certification management. 
            Connecting companies, consultants, and auditors on a single, 
            secure platform for seamless compliance.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-green-200 flex items-center justify-center gap-2"
            >
              Get Certified Now
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2"
            >
              Request Demo
            </button>
          </div>
          
          <div className="mt-12 text-sm text-gray-500">
            Trusted by industry leaders in Halal compliance
          </div>
        </div>
      </section>

      {/* Market Stats Section */}
      <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="p-6">
                <div className="text-4xl md:text-5xl font-bold text-green-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
           <div className="absolute right-0 top-0 w-96 h-96 bg-green-500 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
           <div className="absolute left-0 bottom-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose HALMAS?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A unified platform designed to simplify the complex process of Halal certification
              while ensuring the highest standards of integrity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="bg-gray-50 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
                <a href="#" className="inline-flex items-center mt-6 text-green-600 font-medium hover:text-green-700">
                  Learn more <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlight Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                End-to-End Compliance Management
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Digital Document Vault</h3>
                    <p className="text-gray-600">Securely store and manage all your halal certification documents in one centralized, searchable repository.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                   <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Real-time Tracking</h3>
                    <p className="text-gray-600">Track the status of your applications, audits, and renewals in real-time with automated notifications.</p>
                  </div>
                </div>
                 <div className="flex gap-4">
                   <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                    <Globe2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Global Standards</h3>
                    <p className="text-gray-600">Built to comply with international Halal standards, ensuring your certification is recognized worldwide.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-green-200 to-blue-200 rounded-3xl transform rotate-3 scale-95 opacity-50 blur-lg"></div>
              <div className="relative bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                <div className="space-y-4">
                    {/* Mock UI Elements */}
                    <div className="flex items-center justify-between border-b pb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100"></div>
                            <div>
                                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                                <div className="h-3 w-20 bg-gray-100 rounded mt-2"></div>
                            </div>
                        </div>
                        <div className="h-8 w-24 bg-green-100 rounded-full"></div>
                    </div>
                     <div className="space-y-3">
                        <div className="h-20 bg-gray-50 rounded-lg w-full"></div>
                        <div className="h-20 bg-gray-50 rounded-lg w-full"></div>
                        <div className="h-20 bg-gray-50 rounded-lg w-full"></div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

       <Footer
        logo={
           <div className="h-8 w-8 bg-gradient-to-br from-green-600 to-blue-600 rounded flex items-center justify-center">
             <Check className="w-5 h-5 text-white" />
           </div>
        }
        logoText="HALMAS"
        copyright="© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved."
        sections={[
            {
                title: 'Platform',
                links: [
                    { label: 'Features', href: '#' },
                    { label: 'Pricing', href: '#' },
                    { label: 'Demo', href: '#' },
                ]
            },
             {
                title: 'Company',
                links: [
                    { label: 'About Us', href: '#' },
                    { label: 'Careers', href: '#' },
                    { label: 'Contact', href: '#' },
                ]
            },
             {
                title: 'Legal',
                links: [
                    { label: 'Privacy Policy', href: '#' },
                    { label: 'Terms of Service', href: '#' },
                ]
            }
        ]}
      />
    </div>
  );
}
